import { useEffect, useState } from "react";
import { Lock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/lib/api";
import { useSpark } from "@/lib/store";

export default function PartTwo() {
  const { userId, profile } = useSpark();
  const [recs, setRecs] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/profile/${userId}/recommendations`).then(r => { if (alive) setRecs(r.data); });
    return () => { alive = false; };
  }, [userId]);

  const tap = () => setShowPaywall(true);
  const originalReasons = profile?.starting_point_input?.reasons || [];

  return (
    <div className="space-y-12">
      <header className="max-w-3xl">
        <p className="spark-eyebrow">Make my move</p>
        <h1 className="mt-2 text-5xl sm:text-6xl leading-[1.05] text-[#17171C]">
          Okay. So <span className="font-semibold text-[#2C50A0]">what's next?</span>
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-[#56565D]">
          You've done the thinking. Now let's turn what you've discovered into action.
          Spark surfaces the two or three tools most likely to help — based on what you
          told us about why you're here.
        </p>
      </header>

      {originalReasons.length > 0 && (
        <div className="spark-card p-6 sm:p-7">
          <p className="spark-eyebrow mb-3">Why you came to Spark</p>
          <div className="flex flex-wrap gap-2">
            {originalReasons.map(r => (
              <span key={r} className="rounded-full border border-[rgba(31,27,22,0.12)] bg-white px-3 py-1.5 text-xs text-[#56565D]">{r}</span>
            ))}
          </div>
        </div>
      )}

      <section>
        <div className="flex items-center gap-3 mb-2">
          <span className="spark-eyebrow">A few places Spark thinks would help</span>
          <div className="h-px flex-1 bg-[rgba(31,27,22,0.08)]" />
        </div>
        {recs?.reasoning && (
          <p className="text-[#56565D] leading-relaxed max-w-2xl mb-6">{recs.reasoning}</p>
        )}
        <div className="grid sm:grid-cols-3 gap-4">
          {(recs?.recommended || []).map(t => (
            <button
              key={t.key}
              data-testid={`part-two-recommended-${t.key}`}
              onClick={tap}
              className="text-left spark-card p-7 hover:border-[rgba(176,138,74,0.4)] transition"
            >
              <div className="h-1 w-8 bg-[#2C50A0] mb-4" />
              <h3 className="text-2xl leading-snug text-[#17171C]">{t.title}</h3>
              <p className="mt-2 text-[#56565D] leading-relaxed">{t.blurb}</p>
            </button>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center gap-3 mb-6">
          <span className="spark-eyebrow">Everything else, when you need it</span>
          <div className="h-px flex-1 bg-[rgba(31,27,22,0.08)]" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(recs?.rest || []).map(t => (
            <button
              key={t.key}
              data-testid={`part-two-tool-${t.key}`}
              onClick={tap}
              className="text-left group spark-card p-6 hover:border-[rgba(176,138,74,0.4)] transition"
            >
              <div className="flex items-start justify-between">
                <h3 className="text-xl leading-snug text-[#17171C] group-hover:text-[#1F3D80] transition">{t.title}</h3>
                <Lock size={14} className="text-[#8A8A91] mt-1.5" />
              </div>
              <p className="mt-2 text-sm text-[#56565D] leading-relaxed">{t.blurb}</p>
            </button>
          ))}
        </div>
      </section>

      <section className="spark-card p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div>
          <p className="spark-eyebrow mb-2">Optional</p>
          <p className="text-2xl text-[#17171C]">Want to go deeper?</p>
          <p className="text-[#56565D] mt-1 max-w-lg">
            When the moment feels right, you can book a one-to-one with Kate and share your
            Career Compass ahead of time. Never required — always yours to choose.
          </p>
        </div>
        <button data-testid="part-two-book-coaching-btn" onClick={tap} className="spark-btn-secondary">Explore human coaching</button>
      </section>

      {showPaywall && <Paywall onClose={() => setShowPaywall(false)} />}
    </div>
  );
}

function Paywall({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[rgba(31,27,22,0.45)] backdrop-blur-sm">
      <div className="relative max-w-lg w-full bg-white rounded-3xl border border-[rgba(176,138,74,0.4)] p-8 sm:p-10">
        <div className="absolute top-0 left-8 right-8 h-px bg-[#2C50A0] opacity-60" />
        <p className="spark-eyebrow text-[#1F3D80]">A quiet moment before the practical work</p>
        <h2 className="mt-3 text-3xl leading-tight text-[#17171C]">
          Everything in Spark, one calm payment.
        </h2>
        <p className="mt-4 text-[#56565D] leading-relaxed">
          Continue your Career Compass, unlock every Make My Move tool, and keep your work
          forever. No subscription. Optional one-to-one coaching with Kate available
          separately.
        </p>
        <div className="mt-6 flex items-baseline gap-2">
          <span className="text-4xl text-[#17171C]">£249</span>
          <span className="text-[#8A8A91] text-sm">one-time · lifetime access</span>
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <button
            data-testid="paywall-unlock-btn"
            onClick={() => toast("Stripe checkout is wired up in the next iteration.")}
            className="spark-btn-copper"
          >
            Unlock Spark
          </button>
          <button data-testid="paywall-close-btn" onClick={onClose} className="spark-btn-secondary">Not just yet</button>
        </div>
        <p className="mt-6 text-xs text-[#8A8A91]">
          Payment is a placeholder in this first build — Stripe will be wired up when we're ready to accept live payments.
        </p>
      </div>
    </div>
  );
}
